import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import StudentCards from "./UserProfileCard";
import StudentService from "./Locality/Service/studentService";
import PageTitle from "../../components/PageTitle/PageTitle";

const useStyles = makeStyles((theme) => ({
  page: { paddingBottom: 40 },
  card: {
    borderRadius: 18,
    border: "1px solid #efefef",
    boxShadow: "0 10px 28px rgba(15, 43, 74, 0.06)",
    overflow: "hidden",
    background: "#fff",
  },
  monthHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 20px",
    borderBottom: "1px solid #f1f5f9",
  },
  monthTitle: { fontWeight: 800, color: "#333", fontSize: "1.05rem" },
  session: { fontWeight: 700, color: "#555", fontSize: "0.95rem" },
  filters: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 16,
  },
  dayRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 20px",
    borderBottom: "1px solid #fafafa",
  },
  dayLabel: { color: "#444", fontWeight: 600, fontSize: "0.98rem" },
  markP: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "#0d9488",
    color: "#fff",
    fontWeight: 800,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.95rem",
  },
  markA: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "#b3261e",
    color: "#fff",
    fontWeight: 800,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.95rem",
  },
  markDash: {
    color: "#ea580c",
    fontWeight: 800,
    fontSize: "1.05rem",
    minWidth: 36,
    textAlign: "center",
  },
  statusHint: {
    display: "block",
    fontSize: "0.75rem",
    color: "#8a8a8a",
    fontWeight: 600,
    marginTop: 2,
  },
  todayChip: {
    marginLeft: 8,
    fontSize: "0.7rem",
    fontWeight: 800,
    color: "#1a1a1a",
    background: "#e6e6e6",
    borderRadius: 8,
    padding: "2px 8px",
  },
  empty: { padding: 32, textAlign: "center", color: "#8a8a8a" },
  monthlyCard: {
    marginTop: 20,
    borderRadius: 16,
    border: "1px solid #efefef",
    overflow: "hidden",
  },
  monthlyTitle: {
    fontWeight: 800,
    color: "#0F3B62",
    padding: "14px 18px",
    background: "#fafafa",
    borderBottom: "1px solid #efefef",
  },
}));

function getUserDetails() {
  try {
    return JSON.parse(localStorage.getItem("userDetail") || "{}") || {};
  } catch {
    return {};
  }
}

export default function ParentAttendance() {
  const classes = useStyles();
  const userDetails = useMemo(() => getUserDetails(), []);
  const [studentList, setStudentList] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [month, setMonth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [calendar, setCalendar] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [todayDay, setTodayDay] = useState(null);
  const [listType, setListType] = useState(null); // 'present' | 'absent' | null

  const schooleId = useMemo(() => {
    const id = userDetails?.schooleId || userDetails?.schoolId;
    return id && typeof id === "object" ? id?._id : id;
  }, [userDetails]);

  const currentMonth = Number(calendar?.currentMonth) || 0;
  const currentYear = Number(calendar?.currentYear) || Number(calendar?.year) || 0;

  const monthOptions = useMemo(() => {
    const lastMonth = currentMonth || 12;
    return Array.from({ length: lastMonth }, (_, i) => ({
      value: i + 1,
      label: new Date(2000, i, 1).toLocaleString(undefined, { month: "long" }),
    }));
  }, [currentMonth]);

  useEffect(() => {
    const body = {};
    if (userDetails.role === "PARENT") body.userId = userDetails._id;
    else body.schooleId = schooleId;
    StudentService.findStudentList(body)
      .then((res) => {
        const list = (Array.isArray(res) ? res : []).map((s) => ({
          ...s,
          fullName: `${s.firstName || ""} ${s.lastName || ""}`.trim(),
        }));
        setStudentList(list);
        if (list[0]?._id) setStudentId(list[0]._id);
      })
      .catch(() => setStudentList([]));
  }, [userDetails._id, userDetails.role, schooleId]);

  useEffect(() => {
    if (!studentId) return;
    let cancelled = false;
    setLoading(true);
    const body = { schooleId };
    if (month != null) body.month = month;
    StudentService.getStudentAttendanceSummary(studentId, body)
      .then((res) => {
        if (cancelled) return;
        setMonthly(Array.isArray(res?.data) ? res.data : []);
        setCalendar(res?.calendar || null);
        // Keep today's row even when another month is selected afterwards.
        const today = (res?.calendar?.days || []).find((d) => d.isToday);
        if (today) setTodayDay(today);
      })
      .catch(() => {
        if (cancelled) return;
        setMonthly([]);
        setCalendar(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [studentId, schooleId, month]);

  const isPresentDay = (d) => d.type === "present" || d.present;
  const isAbsentDay = (d) => d.type === "absent" || d.absent;

  const listedDays = useMemo(() => {
    if (!listType) return [];
    const match = listType === "present" ? isPresentDay : isAbsentDay;
    return (calendar?.days || []).filter((d) => !d.isFuture && match(d));
  }, [calendar, listType]);

  const toggleList = (type) => {
    setListType((prev) => (prev === type ? null : type));
  };

  const monthLabel = (y, m) =>
    new Date(Number(y), Number(m) - 1, 1).toLocaleString(undefined, {
      month: "long",
      year: "numeric",
    });

  const selectedMonth = month != null ? month : Number(calendar?.month) || 1;
  const selectedYear = currentYear || Number(calendar?.year) || 0;

  const monthRow = useMemo(
    () =>
      (Array.isArray(monthly) ? monthly : []).find(
        (row) =>
          Number(row?._id?.year) === Number(selectedYear) &&
          Number(row?._id?.month) === Number(selectedMonth),
      ) || null,
    [monthly, selectedYear, selectedMonth],
  );

  return (
    <Container maxWidth="md" className={classes.page}>
      <PageTitle title="Attendance" />
      {studentList.length > 1 ? (
        <Box mb={2}>
          <StudentCards
            studentList={studentList}
            onStudentSelect={(id) => setStudentId(id)}
            selectedStudentId={studentId}
          />
        </Box>
      ) : null}

      <Box className={classes.filters}>
        <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
          <InputLabel>Month</InputLabel>
          <Select
            label="Month"
            value={selectedMonth}
            onChange={(e) => setMonth(Number(e.target.value))}
          >
            {monthOptions.map((m) => (
              <MenuItem key={m.value} value={m.value}>
                {m.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Card className={classes.card} style={{ maxWidth: 600 }}>
        <Box className={classes.monthHead}>
          <Typography className={classes.monthTitle}>
            {calendar?.monthLabel || "Attendance details"}
          </Typography>
          <Typography className={classes.session}>{calendar?.session || "Attendance Status"}</Typography>
        </Box>
        {loading ? (
          <Box className={classes.empty}>
            <CircularProgress size={32} />
          </Box>
        ) : !todayDay ? (
          <Typography className={classes.empty}>No attendance for today.</Typography>
        ) : (
          <Box className={classes.dayRow}>
            <Box>
              <Typography className={classes.dayLabel}>
                {todayDay.displayDate}
                <span className={classes.todayChip}>Today</span>
              </Typography>
            </Box>
            {isPresentDay(todayDay) ? (
              <span className={classes.markP}>P</span>
            ) : isAbsentDay(todayDay) ? (
              <span className={classes.markA}>A</span>
            ) : (
              <span className={classes.markDash}>___</span>
            )}
          </Box>
        )}
      </Card>

      <Card className={classes.monthlyCard}>
        <Typography className={classes.monthlyTitle}>Monthly summary</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Month</TableCell>
              <TableCell>Present</TableCell>
              <TableCell>Absent</TableCell>
              <TableCell>Total marked</TableCell>
              <TableCell>Present %</TableCell>
              <TableCell>Absent %</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell style={{ fontWeight: 700 }}>
                {monthLabel(selectedYear, selectedMonth)}
              </TableCell>
              <TableCell
                onClick={() => toggleList("present")}
                style={{
                  color: "#1f8a5b",
                  fontWeight: 700,
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                {monthRow?.presentCount ?? 0}
              </TableCell>
              <TableCell
                onClick={() => toggleList("absent")}
                style={{
                  color: "#b3261e",
                  fontWeight: 700,
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                {monthRow?.absentCount ?? 0}
              </TableCell>
              <TableCell>{monthRow?.totalCount ?? 0}</TableCell>
              <TableCell>{monthRow?.presentPercentage ?? 0}%</TableCell>
              <TableCell>{monthRow?.absentPercentage ?? 0}%</TableCell>
            </TableRow>
          </TableBody>
        </Table>
        {listType ? (
          <Box p={2} style={{ borderTop: "1px solid #efefef" }}>
            <Typography style={{ fontWeight: 700, marginBottom: 8 }}>
              {listType === "present" ? "Present dates" : "Absent dates"} –{" "}
              {monthLabel(selectedYear, selectedMonth)}
            </Typography>
            {listedDays.length === 0 ? (
              <Typography style={{ color: "#8a8a8a" }}>No dates.</Typography>
            ) : (
              listedDays.map((d) => (
                <Typography key={d.date} className={classes.dayLabel} style={{ padding: "4px 0" }}>
                  {d.displayDate}
                </Typography>
              ))
            )}
          </Box>
        ) : null}
      </Card>
    </Container>
  );
}
